/*
  사원증 결제정보 실패 목록 엑셀

  양식과 서식은 기획에서 내려온 견본 파일을 그대로 따른다.
    [메타정보]        로고 → 문서명 → 파일명/출처/다운로드 날짜 → 개인정보보호 → 안내사항
    [사원증 결제정보]  제목 → 빈 줄 → 헤더 → 데이터

  이 파일을 고쳐 그대로 다시 올릴 수 있어야 한다. 데이터가 2번째 탭에 있어야 하고
  헤더 이름이 excelParser 의 COLUMN_MATCHERS 와 맞아야 한다.
  normalizeHeader 가 괄호 꼬리말과 공백(줄바꿈 포함)을 떼므로 '(수정 불가)' 표시는 읽기에 영향이 없다.

  셀 서식을 저장해야 해서 읽기(xlsx)와 달리 쓰기는 exceljs 를 쓴다 — xlsx 무료판은 서식을 저장하지 않는다.
*/
import ExcelJS from 'exceljs';
import JSZip from 'jszip';

// 런타임에 이미지 바이트가 필요하다. URL 로 받으면 따로 내려받아야 하므로 data URI 로 심는다.
import logo from 'images/loginLogo.png?inline';

const META_SHEET_NAME = '메타정보';
const DATA_SHEET_NAME = '사원증 결제정보';
const DOCUMENT_TITLE = '사원증 결제정보 실패 목록';
const SOURCE_URL = 'https://corp.mealc.co.kr';

const FONT_NAME = '맑은 고딕';
const READONLY_MARK = '(수정 불가)';
const EDITABLE_MARK = '(수정 가능)';
const READONLY_MARK_COLOR = 'FFFF0000';
const EDITABLE_MARK_COLOR = 'FF0000FF';
const EDITABLE_HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEFED8' } };
const EDITABLE_CELL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
// 고칠 수 없는 열은 회색으로 덮어 '변경할 사원증 결제정보' 한 열만 손대면 된다는 걸 드러낸다.
const LOCKED_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
const THIN = { style: 'thin' };
const MEDIUM = { style: 'medium' };

/*
  사원증 결제정보는 0 으로 시작하거나 숫자만 있는 값이 있어 엑셀이 수로 바꾸면 값이 달라진다.
  견본과 같이 표의 모든 칸을 텍스트 서식으로 저장한다.
*/
const TEXT_FORMAT = '@';

const INPUT_RULE_TEXT =
  '- 영문 대문자·숫자·하이픈(-)만 입력 가능 (소문자·공백은 자동 변환됩니다.)\n' +
  '- 최대 30자 입력 가능, 중복 불가\n' +
  '- 삭제를 원하는 경우 공란으로 등록';

// 고쳐도 되는 칸에 입력 규칙을 셀 메모로 붙인다(견본과 같은 문구·글꼴).
const EDITABLE_HEADER_NOTE = {
  texts: [
    {
      font: { size: 10, color: { indexed: 81 }, name: FONT_NAME, family: 3, charset: 129, scheme: 'major' },
      text: INPUT_RULE_TEXT
    }
  ]
};

/*
  exceljs 는 메모를 항상 숨김으로만 쓴다 — vml-shape-xform 의 style 에 visibility:hidden 이 박혀 있고
  '항상 표시'를 뜻하는 x:Visible 도 내보내지 않는다. 옵션으로 바꿀 수 없어,
  다 만들어진 파일을 열어 VML 의 그 두 군데만 견본과 같게 고친다(showNotesAlways).
  위치·크기도 숨김 기준의 기본값이라 늘 펼쳐두면 표를 가려, 견본의 값으로 함께 바꾼다.
  입력 규칙 1번째 줄이 길어져(10pt 기준 약 334pt) 견본 폭(E~F열)으로는 줄바꿈되며 마지막 줄이 잘린다.
  높이는 그대로 두고 오른쪽으로 H열 일부까지 넓힌다 — 제목·여백 행 위라 표를 가리지 않는다.
*/
const VML_PATH = /^xl\/drawings\/vmlDrawing\d+\.vml$/;
const VML_HIDDEN_STYLE =
  'position:absolute; margin-left:105.3pt;margin-top:10.5pt;width:97.8pt;height:59.1pt;z-index:1;visibility:hidden';
const VML_VISIBLE_STYLE =
  'position:absolute;margin-left:326.4pt;margin-top:.6pt;width:414.8pt;height:51pt;z-index:1;' +
  'visibility:visible;mso-wrap-style:tight';
const VML_VISIBLE_ANCHOR = '4, 0, 0, 0, 7, 95, 1, 28';

const showNotesAlways = async (buffer) => {
  const zip = await JSZip.loadAsync(buffer);
  const names = Object.keys(zip.files).filter((name) => VML_PATH.test(name));

  await Promise.all(
    names.map(async (name) => {
      const vml = await zip.file(name).async('string');

      zip.file(
        name,
        vml
          .replace(VML_HIDDEN_STYLE, VML_VISIBLE_STYLE)
          .replace(/<x:Anchor>[^<]*<\/x:Anchor>/g, `<x:Anchor>${VML_VISIBLE_ANCHOR}</x:Anchor>`)
          .replace(/<\/x:ClientData>/g, '<x:Visible/></x:ClientData>')
      );
    })
  );

  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
};

const PRIVACY_WARNING =
  '※ 본 정보는 식권대장 서비스 외 사용을 엄격히 금지합니다. \n※ 계약 시 작성한 개인정보보호정책을 준수합니다. ';
const PRIVACY_CONTACT =
  '\nㅡ\n대표전화 : 1644-5047\n이메일 : biz@vendys.co.kr\n서울특별시 강남구 봉은사로103길 5, 4층, 6층\nㅡ\n식권대장 개인정보보호담당자 : 한선호CTO \nprivacy@vendys.co.kr | 1644-5047 ';
const GUIDE_TEXT = '변경사항이 없거나 개인식별 번호가 없는 경우 수정에서 제외됩니다.';

const PRIVACY_VALUE = {
  richText: [
    { font: { name: FONT_NAME, size: 12, color: { argb: READONLY_MARK_COLOR } }, text: PRIVACY_WARNING },
    { font: { name: FONT_NAME, size: 12 }, text: PRIVACY_CONTACT }
  ]
};

/*
  개인정보보호 안내는 줄 수가 많아 행 높이를 넉넉히 잡아야 한다.
  12pt 글자의 실제 줄 높이는 약 16pt 지만, 맑은 고딕이 없는 환경에서 대체 글꼴이 더 크게 그려지면
  고정 높이를 넘겨 끝줄이 잘린다. 여유를 둔 줄 높이로 문구의 줄 수에서 직접 계산한다.
  (문구를 고치면 높이도 따라간다)
*/
const PRIVACY_LINE_HEIGHT = 28;
const PRIVACY_ROW_HEIGHT = `${PRIVACY_WARNING}${PRIVACY_CONTACT}`.split('\n').length * PRIVACY_LINE_HEIGHT;

const buildMetaSheet = (workbook, fileName, downloadedAt) => {
  const sheet = workbook.addWorksheet(META_SHEET_NAME);

  sheet.columns = [{ width: 6.25 }, { width: 40.125 }, { width: 76.5 }, { width: 9.75 }];

  // 1행은 로고 자리다.
  sheet.getRow(1).height = 88.9;
  sheet.addImage(workbook.addImage({ base64: logo, extension: 'png' }), {
    tl: { col: 1.25, row: 0.34 },
    ext: { width: 87, height: 54 },
    editAs: 'oneCell'
  });

  // 라벨(B)과 값(C) 두 칸이 위아래 선으로 한 줄처럼 묶인다.
  const writeRow = (rowNumber, height, label, value, options) => {
    const { size, isTitle, wrapText } = options || {};
    const row = sheet.getRow(rowNumber);

    row.height = height;

    [2, 3].forEach((columnNumber) => {
      const cell = row.getCell(columnNumber);
      const isLabel = columnNumber === 2;

      cell.font = { name: FONT_NAME, size: size || 12, bold: !!isTitle || isLabel };
      cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1, wrapText: !isLabel && !!wrapText };
      cell.border = { top: THIN, bottom: THIN };
    });

    row.getCell(2).value = label;
    if (value) row.getCell(3).value = value;
  };

  writeRow(2, 45, DOCUMENT_TITLE, null, { size: 16, isTitle: true });
  writeRow(3, 24, '파일명', fileName);
  writeRow(4, 24, '출처', SOURCE_URL);
  writeRow(5, 24, '다운로드 날짜', downloadedAt);

  // 견본에서 이 칸만 글자색·배경이 따로 지정돼 있다(기본값과 같은 검정/흰색).
  const downloadedAtCell = sheet.getRow(5).getCell(3);

  downloadedAtCell.font = { name: FONT_NAME, size: 12, color: { argb: 'FF000000' } };
  downloadedAtCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
  downloadedAtCell.numFmt = TEXT_FORMAT;

  sheet.getRow(6).height = 24;

  writeRow(7, PRIVACY_ROW_HEIGHT, '개인정보보호', PRIVACY_VALUE, { wrapText: true });
  writeRow(8, 18, '안내사항', GUIDE_TEXT, { wrapText: true });

  sheet.getRow(9).height = 16.15;
};

// 원본 '#' 을 유지해야 이 파일을 고쳐 다시 올려도 번호가 흔들리지 않는다.
// 원본에 번호가 없으면 지어내지 않고 화면과 같이 빈 값으로 둔다.
const resultIndex = (row) => {
  const excelIndex = parseInt(row.excelIndex, 10);
  return excelIndex > 0 ? excelIndex : '-';
};

const DATA_COLUMNS = [
  { header: '#', width: 7.25, align: 'center', value: resultIndex },
  { header: '개인식별 번호', width: 15.75, mark: READONLY_MARK, value: (row) => row.userId || '' },
  { header: 'ID', width: 15.75, mark: READONLY_MARK, value: (row) => row.signId || '' },
  { header: '이름', width: 15.75, mark: READONLY_MARK, value: (row) => row.name || '' },
  { header: '현재 사원증 결제정보', width: 20.75, mark: READONLY_MARK, value: (row) => row.currentBarCode || '' },
  {
    header: '변경할 사원증 결제정보',
    width: 20.75,
    mark: EDITABLE_MARK,
    isEditable: true,
    value: (row) => row.barCode || ''
  },
  // 실패한 건만 내려주므로 저장 결과는 전부 실패다.
  { header: '저장 결과', width: 15.75, mark: READONLY_MARK, value: () => '실패' },
  { header: '비고', width: 43.25, mark: READONLY_MARK, value: (row) => row.message || '' }
];

const DATA_START_ROW = 4;

// 헤더는 '이름 + 줄바꿈 + 표시' 두 조각으로 나눠 표시에만 색을 준다.
const headerValue = (column) => {
  if (!column.mark) return column.header;

  const color = column.isEditable ? EDITABLE_MARK_COLOR : READONLY_MARK_COLOR;

  return {
    richText: [
      { font: { name: FONT_NAME, size: 10, bold: true }, text: column.header },
      { font: { name: FONT_NAME, size: 10, bold: true, color: { argb: color } }, text: `\n${column.mark}` }
    ]
  };
};

const buildDataSheet = (workbook, rows) => {
  const sheet = workbook.addWorksheet(DATA_SHEET_NAME);

  sheet.columns = DATA_COLUMNS.map((column) => ({ width: column.width }));

  const titleRow = sheet.getRow(1);

  titleRow.height = 30;
  titleRow.font = { name: FONT_NAME, size: 11 };

  const titleCell = titleRow.getCell(2);

  titleCell.value = DOCUMENT_TITLE;
  titleCell.font = { name: FONT_NAME, size: 18, bold: true };
  titleCell.alignment = { vertical: 'middle' };
  titleCell.numFmt = TEXT_FORMAT;

  // 2행은 제목과 표 사이 여백이다.
  sheet.getRow(2).height = 30;
  sheet.getRow(2).font = { name: FONT_NAME, size: 11 };

  const headerRow = sheet.getRow(3);

  headerRow.height = 31.9;
  DATA_COLUMNS.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);

    cell.value = headerValue(column);
    cell.font = { name: FONT_NAME, size: 10, bold: true };
    cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: !!column.mark };
    cell.border = { top: MEDIUM, bottom: MEDIUM };
    cell.numFmt = TEXT_FORMAT;
    cell.fill = column.isEditable ? EDITABLE_HEADER_FILL : LOCKED_FILL;
    if (column.isEditable) cell.note = EDITABLE_HEADER_NOTE;
  });

  rows.forEach((row, index) => {
    const dataRow = sheet.getRow(DATA_START_ROW + index);

    dataRow.height = 15.6;
    DATA_COLUMNS.forEach((column, columnIndex) => {
      const cell = dataRow.getCell(columnIndex + 1);

      cell.value = column.value(row, index);
      cell.font = { name: FONT_NAME, size: 10 };
      cell.alignment = { horizontal: column.align, vertical: 'middle' };
      cell.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
      cell.numFmt = TEXT_FORMAT;
      cell.fill = column.isEditable ? EDITABLE_CELL_FILL : LOCKED_FILL;
    });
  });
};

const writeEmployeeCardPaymentResultExcel = (rows, { fileName, downloadedAt }) => {
  const workbook = new ExcelJS.Workbook();

  buildMetaSheet(workbook, fileName, downloadedAt);
  buildDataSheet(workbook, rows);

  return workbook.xlsx.writeBuffer().then(showNotesAlways);
};

export default writeEmployeeCardPaymentResultExcel;
